/**
 * Route guard for a single use case: renders its child only if this organization was given that use case.
 *
 * WHY IT EXISTS. The landing tiles have always been filtered by the backend's `allowedUseCases`, but the routes
 * were not, so a surface a client was never given stayed reachable by typing its URL. Found on a replica of
 * production: an Amy's account with `survey-chat-154` and `vitamin-concept-test` had no Focus Group tile and still
 * rendered the entire Focus Group chat at /ask-natural-organic. The server-side gate now 403s every request behind
 * it, so what remained was a dead page rather than an absent one — this makes it absent.
 *
 * Deliberately NOT a second copy of the rule. The list comes from the same `useSubscription().allowedUseCases`
 * the tiles read, so a client's tiles and their reachable routes cannot disagree.
 */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { isUnrestrictedUseCases } from '../config/surfaceUseCases';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/subscription/SubscriptionContext';

interface UseCaseRouteProps {
  /** Every use case that opens this surface. More than one can: hbs holds `hbs-chat` where other clients hold
   *  `healthcare-chat-v2`, and both tiles navigate here. Gating on one name would revoke a working surface. */
  useCases: readonly string[];
  children: React.ReactNode;
  /** Where to send someone who may not be here. The dashboard shows what they CAN do, which is kinder than a 403. */
  redirectTo?: string;
}

const UseCaseRoute: React.FC<UseCaseRouteProps> = ({ useCases, children, redirectTo = '/' }) => {
  const { allowedUseCases, entitlementsLoading } = useSubscription();
  const { user } = useAuth();
  const location = useLocation();

  // NOT YET KNOWN is not the same as NOT ALLOWED, and conflating them is the trap `entitlementsLoading` was added
  // for: the dashboard once drew every card locked with an upsell because "unknown" and "unrestricted and unpaid"
  // were the same value. Redirecting here would throw a legitimate client to the dashboard on every slow load.
  if (entitlementsLoading) {
    return null;
  }

  // An admin reaches every surface, matching the backend gate. An internal account that cannot open a client's
  // screen cannot reproduce that client's problem.
  if (user?.is_admin) {
    return <>{children}</>;
  }

  // Absent, the sentinel, or an EMPTY list all mean unrestricted — never "nothing". One shared predicate with the
  // tile filter, because a guard that disagrees with the tiles produces the dead-end click this component exists to
  // prevent. The empty-array case is the one that was missed, and review of #734 caught it.
  if (isUnrestrictedUseCases(allowedUseCases)) {
    return <>{children}</>;
  }

  if (Array.isArray(allowedUseCases) && useCases.some((name) => allowedUseCases.includes(name))) {
    return <>{children}</>;
  }

  return <Navigate to={redirectTo} state={{ from: location, deniedUseCases: useCases }} replace />;
};

export default UseCaseRoute;
